// minimizedSlots — pure list + persistence logic for the shell PageSlot's
// minimize-to-dock feature (2026-08-06 per Nika "we should support minimize
// feature for apps, to continue exactly where it left off").
//
// The REACTIVE side lives in canvas/components/PageSlot.js (keep-alive hosts,
// MinimizeBtn) + canvas/components/MinimizedDock.js (the chips bar); this
// module owns only the decisions that are pure data → data, so they are
// testable without a DOM and can never drift between the two consumers:
//
//   • `root._minimizedSlots` shape — ordered array (oldest → newest) of
//     { href, title, icon, wsId, minimizedAt } entries, cap 5 (minimizing a
//     6th evicts the oldest — the caller unmounts the evicted host).
//   • per-workspace sessionStorage persistence of the LIST ONLY (the mounted
//     keep-alive hosts are in-memory by nature): after a reload the dock
//     offers the same apps as COLD restores (fresh mount, best-effort
//     continuation). Per-tab sessionStorage matches the per-tab workspace
//     pointer convention (activeWorkspacePointer.js).
//
// Lives in shared/functions (not workspace/) for the same reason openInSlot
// does: PageSlot renders under the shell but ships in the canvas package,
// which links shared and never workspace. `functions/index.js` DOES barrel
// this file (`export * from './minimizedSlots.js'`) — openInSlot.js's own
// `import { cleanHref } from './minimizedSlots.js'` is a plain cross-file
// ESM import, which is safe (resolves via frank's globalScope fallback).
//
// FA204 / frank trap — _validEntry/STORAGE_PREFIX/_storage used to live at
// module scope, plus bare sibling-export calls (cleanHref/
// minimizedSlotsStorageKey/MINIMIZED_SLOTS_CAP) between the exports below.
// Each export now carries its own self-contained inline copy of whichever
// locals/siblings it actually calls.

export const MINIMIZED_SLOTS_CAP = 5

// Route identity is the QUERYLESS href — one dock entry / one keep-alive host
// per route (matches PageSlot's `_defFor` resolution, which also splits '?').
export const cleanHref = (href) =>
  String(href || '')
    .split('?')[0]
    .split('#')[0]

export const hasMinimizedEntry = (list, href) => {
  const _cleanHref = (h) =>
    String(h || '')
      .split('?')[0]
      .split('#')[0]
  const _validEntry = (en) =>
    !!(en && typeof en === 'object' && typeof en.href === 'string' && en.href)

  const clean = _cleanHref(href)
  return Array.isArray(list) && list.some((en) => _validEntry(en) && _cleanHref(en.href) === clean)
}

// Park an entry: dedupe by clean href (a re-minimize replaces the old entry —
// fresh `minimizedAt`, moves to the newest end), append, then evict oldest
// past the cap. Returns { list, evicted } — the caller owns unmounting the
// evicted hosts (fully, not keep-alive) and persisting the new list.
// No default on `cap` — line below (`Number.isFinite(cap) && cap > 0 ? cap :
// MINIMIZED_SLOTS_CAP`) already falls back identically for `undefined`, and
// putting the module constant IN the default position made frank's
// `to-json` rewrite the emitted signature text, tripping the `functions`
// arity-contract check as `signature-drift` even though arity never
// changed (same class as authGuard.js's DEFAULT_AUTH_PATHS — see there for
// detail).
export const minimizeEntry = (list, entry, cap) => {
  const MINIMIZED_SLOTS_CAP = 5
  const _cleanHref = (h) =>
    String(h || '')
      .split('?')[0]
      .split('#')[0]
  const _validEntry = (en) =>
    !!(en && typeof en === 'object' && typeof en.href === 'string' && en.href)

  if (!_validEntry(entry)) return { list: Array.isArray(list) ? list : [], evicted: [] }
  const clean = _cleanHref(entry.href)
  const base = (Array.isArray(list) ? list : []).filter(
    (en) => _validEntry(en) && _cleanHref(en.href) !== clean
  )
  const next = [...base, entry]
  const max = Number.isFinite(cap) && cap > 0 ? cap : MINIMIZED_SLOTS_CAP
  if (next.length <= max) return { list: next, evicted: [] }
  // Oldest first by position — the list is kept in minimize order, so the
  // front IS the oldest (`minimizedAt` is carried for display/debug, not
  // re-sorted: a caller-supplied clock can't reorder identity).
  return { list: next.slice(next.length - max), evicted: next.slice(0, next.length - max) }
}

// Drop an entry (restore or explicit dock-close). Pure — the caller decides
// whether the host is revealed (restore) or removed (close).
export const removeMinimizedEntry = (list, href) => {
  const _cleanHref = (h) =>
    String(h || '')
      .split('?')[0]
      .split('#')[0]
  const _validEntry = (en) =>
    !!(en && typeof en === 'object' && typeof en.href === 'string' && en.href)

  const clean = _cleanHref(href)
  return (Array.isArray(list) ? list : []).filter(
    (en) => _validEntry(en) && _cleanHref(en.href) !== clean
  )
}

// ── Per-workspace persistence (sessionStorage, per-tab) ────────────────────

export const minimizedSlotsStorageKey = (wsId) => 'minimizedSlots.' + String(wsId || 'default')

// Read the persisted list for a workspace. Defensive on every axis a storage
// blob can rot: missing key, corrupt JSON, non-array, foreign-shape entries,
// and entries stamped with ANOTHER workspace's id (a tenant-isolation
// backstop — the key is already per-workspace, the stamp check makes a
// mis-keyed write inert instead of a leak).
export const readMinimizedList = (wsId, storage) => {
  const MINIMIZED_SLOTS_CAP = 5
  const _minimizedSlotsStorageKey = (id) => 'minimizedSlots.' + String(id || 'default')
  const _validEntry = (en) =>
    !!(en && typeof en === 'object' && typeof en.href === 'string' && en.href)
  const _storage = (storage) => {
    if (storage) return storage
    try {
      return globalThis.sessionStorage || null
    } catch (_) {
      return null
    }
  }

  const store = _storage(storage)
  if (!store || !wsId) return []
  let raw = null
  try {
    raw = store.getItem(_minimizedSlotsStorageKey(wsId))
  } catch (_) {
    return []
  }
  if (!raw) return []
  let parsed = null
  try {
    parsed = JSON.parse(raw)
  } catch (_) {
    return []
  }
  if (!Array.isArray(parsed)) return []
  return parsed
    .filter(_validEntry)
    .filter((en) => !en.wsId || String(en.wsId) === String(wsId))
    .slice(0, MINIMIZED_SLOTS_CAP)
}

// Persist the list for a workspace (remove the key when empty so storage
// never accumulates dead tenants). Never throws — persistence is best-effort
// by design (quota, privacy mode).
export const writeMinimizedList = (wsId, list, storage) => {
  const _minimizedSlotsStorageKey = (id) => 'minimizedSlots.' + String(id || 'default')
  const _validEntry = (en) =>
    !!(en && typeof en === 'object' && typeof en.href === 'string' && en.href)
  const _storage = (storage) => {
    if (storage) return storage
    try {
      return globalThis.sessionStorage || null
    } catch (_) {
      return null
    }
  }

  const store = _storage(storage)
  if (!store || !wsId) return
  try {
    const entries = (Array.isArray(list) ? list : []).filter(_validEntry)
    if (!entries.length) store.removeItem(_minimizedSlotsStorageKey(wsId))
    else store.setItem(_minimizedSlotsStorageKey(wsId), JSON.stringify(entries))
  } catch (_) {
    /* best-effort */
  }
}

// Display title for slot chrome — the PageSlot header and the dock chips
// show the page's OWN name only ("Inbox", not "Inbox / Symbols Workspace");
// the full helmet form stays on document.title / the browser tab (2026-08-06
// per Nika "keep it in <title> and tab title - but cut pageslot or minimized
// title"). First ' / '-segment of the helmet title — applied at RENDER time
// too, so entries persisted with long titles display short without a
// migration.
export const shortSlotTitle = (title) => {
  const t = String(title || '').trim()
  if (!t) return t
  const cut = t.split(' / ')[0].trim()
  return cut || t
}
